import React, { useEffect, useState, useMemo } from 'react';
import { fetchCo2Data, fetchNewCostQuantityTypes, fetchRegions } from '../services/api';
import { useRegion } from '../context/RegionContext';
import './PageLayout.css';

function Co2Page() {
    const { selectedRegion } = useRegion();
    
    const [materials, setMaterials] = useState([]);
    const [filters, setFilters] = useState({});
    const [categories, setCategories] = useState([]);
    const [quantityTypes, setQuantityTypes] = useState([]);
    const [availableRegions, setAvailableRegions] = useState([]);
    const [loading, setLoading] = useState(false);

    // ✅ Load available regions
    useEffect(() => {
        (async () => {
            try {
                const regions = await fetchRegions();
                console.log('🔍 CO2 PAGE: Fetched regions:', regions);
                setAvailableRegions(Array.isArray(regions) ? regions : []);
            } catch (e) {
                console.error('Error loading regions:', e);
                setAvailableRegions([]);
            }
        })();
    }, []);

    // ✅ Get region name for display
    const regionName = useMemo(() => {
        if (!Array.isArray(availableRegions) || availableRegions.length === 0) {
            return selectedRegion;
        }
        const r = availableRegions.find((r) => r.id === selectedRegion);
        return r ? r.name : selectedRegion;
    }, [availableRegions, selectedRegion]);

    // ✅ Load data when region changes
    useEffect(() => {
        if (!selectedRegion) {
            console.log('🔍 CO2 PAGE: No selectedRegion yet');
            return;
        }

        const loadData = async () => {
            setLoading(true);
            try {
                console.log('🔍 CO2 PAGE: Loading data for region:', selectedRegion);
                
                // ✅ Pass region to fetchCo2Data
                const response = await fetchCo2Data(selectedRegion);
                console.log("📌 Fetched Co2, Biogenic, and Weight data:", response.data);
                
                let data = [];
                if (response && Array.isArray(response.data)) {
                    data = response.data;
                } else if (Array.isArray(response)) {
                    data = response;
                } else {
                    console.warn('CO2 data is not an array:', response);
                    data = [];
                }
                
                setMaterials(data);

                // Extract unique categories from the data
                const uniqueCategories = Array.from(new Set(data.map(item => item.Category))).filter(Boolean);
                setCategories(uniqueCategories);

            } catch (error) {
                console.error('❌ Error fetching CO2 data:', error);
                setMaterials([]);
                setCategories([]);
            } finally {
                setLoading(false);
            }
        };

        const loadQuantityTypes = async () => {
            try {
                console.log('🔍 CO2 PAGE: Loading quantity types for region:', selectedRegion);
                const quantityTypesData = await fetchNewCostQuantityTypes(selectedRegion);
                console.log("📌 Quantity Types:", quantityTypesData);
                setQuantityTypes(Array.isArray(quantityTypesData) ? quantityTypesData : []);
            } catch (error) {
                console.error('❌ Error fetching quantity types:', error);
                setQuantityTypes([]);
            }
        };

        loadData();
        loadQuantityTypes();
    }, [selectedRegion]); // ✅ Reload when region changes

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters({
            ...filters,
            [name]: value === 'All' ? undefined : value, 
        });
    };

    // ✅ Defensive filtering
    const filteredMaterials = Array.isArray(materials) ? materials.filter((material) => {
        return (
            (filters.MeasurementUnitRateId === undefined || material.MeasurementUnitRateId?.toString().includes(filters.MeasurementUnitRateId)) &&
            (filters.MeasurementUnitName === undefined || material.MeasurementUnitName?.toLowerCase().includes(filters.MeasurementUnitName?.toLowerCase() || '')) &&
            (filters.Category === undefined || material.Category === filters.Category) && 
            (filters.QuantityTypeName === undefined || material.QuantityTypeName?.toLowerCase().includes(filters.QuantityTypeName?.toLowerCase() || '')) 
        );
    }) : [];

    return (
        <div>
            {/* ✅ Add page title section with region indicator */}
            <div className="page-title-section">
                <h1>Co2, Biogenic, and Weight Data</h1>
                <div className="region-indicator-under-title">
                    🌏 Current Region: <strong>{regionName}</strong>
                </div>
            </div>

            <div className="supply-rates-container">
                <div className="filter-section">
                    <h3>Filters</h3>
                    
                    <div className="filter-group">
                        <label htmlFor="MeasurementUnitRateId">ID</label>
                        <input
                            type="text"
                            name="MeasurementUnitRateId"
                            value={filters.MeasurementUnitRateId || ''}
                            onChange={handleFilterChange}
                            placeholder="Filter by ID"
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="MeasurementUnitName">Name</label>
                        <input
                            type="text"
                            name="MeasurementUnitName"
                            value={filters.MeasurementUnitName || ''}
                            onChange={handleFilterChange}
                            placeholder="Filter by Name"
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="Category">Category</label>
                        <select
                            name="Category"
                            value={filters.Category || 'All'}
                            onChange={handleFilterChange}
                        >
                            <option value="All">All</option>
                            {Array.isArray(categories) && categories.map((category, index) => (
                                <option key={index} value={category}>
                                    {category}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label htmlFor="QuantityTypeName">Quantity Type</label>
                        <select
                            name="QuantityTypeName"
                            value={filters.QuantityTypeName || 'All'}
                            onChange={handleFilterChange}
                        >
                            <option value="All">All</option>
                            {Array.isArray(quantityTypes) && quantityTypes.map((qt, index) => (
                                <option key={qt.Id || index} value={qt.Id}>
                                    {qt.Name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="table-section">
                    {loading ? (
                        <p>Loading CO2 data for {regionName}...</p>
                    ) : filteredMaterials.length === 0 ? (
                        <p>No CO2 data found for {regionName}...</p>
                    ) : (
                        <table className="supply-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Category</th>
                                    <th>Name</th>
                                    <th>Quantity Type</th>
                                    <th>Co2</th> 
                                    <th>Biogenic</th> 
                                    <th>Weight</th> 
                                </tr>
                            </thead>
                            <tbody>
                                {filteredMaterials.map((rate, index) => (
                                    <tr key={rate.MeasurementUnitRateId || index}>
                                        <td>{rate.MeasurementUnitRateId}</td>
                                        <td>{rate.Category}</td>
                                        <td>{rate.MeasurementUnitName}</td>
                                        <td>{rate.QuantityTypeName}</td>
                                        <td>{rate.Co2Value || '0.00000'}</td>
                                        <td>{rate.BiogenicValue || '0.00000'}</td>
                                        <td>{rate.WeightValue || '0.00000'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Co2Page;
